import {useState, useRef, useEffect, useCallback, useMemo, CSSProperties} from "react";
import {useFormContext} from "react-hook-form";
import {Badge} from "@/components/ui/badge";
import {X, Plus} from "lucide-react";
import {cn} from "@/lib/utils";
import {getInvestigationsForSpecialty} from "@/lib/constants";
import SectionPropertiesProps from "@/components/shared/SectionProperties.tsx";

const getInvestigationName = (item: any): string =>
    typeof item === "string" ? item : typeof item?.investigation === "string" ? item.investigation : "";

export default function InvestigationsGrid({specialty = "General Medicine"}: SectionPropertiesProps) {
    const {setValue, watch, formState: {errors}} = useFormContext();
    const [searchInput, setSearchInput] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const getDropdownStyle = (): CSSProperties => {
        if (!inputRef.current) return {};
        const rect = inputRef.current.getBoundingClientRect();
        const dropdownHeight = Math.min(8 * 36 + 8, 224);
        const wouldOverflowBottom = rect.bottom + dropdownHeight > window.innerHeight - 20;
        const wouldOverflowRight = rect.left + 280 > window.innerWidth - 20;
        return {
            position: "fixed",
            minWidth: 280,
            zIndex:   9999,
            ...(wouldOverflowRight ? { right: window.innerWidth - rect.right } : { left: rect.left }),
            ...(wouldOverflowBottom
                ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                : { top: rect.bottom + 2 }),
        };
    };

    // 💡 Native Array Watcher logic (Matches your dedicated relational table)
    const rawSelected: any[] = watch("investigations") || [];

    const selectedTests = useMemo(() => {
        return rawSelected.map(getInvestigationName).filter(Boolean);
    }, [rawSelected]);

    const allOptions: string[] = useMemo(() => {
        const categories = getInvestigationsForSpecialty(specialty);
        const seen = new Set<string>();
        const result: string[] = [];
        for (const tests of Object.values(categories)) {
            if (!Array.isArray(tests)) continue;
            for (const test of tests) {
                if (!seen.has(test)) {
                    seen.add(test);
                    result.push(test);
                }
            }
        }
        return result;
    }, [specialty]);

    const filteredSuggestions = useMemo(() => {
        const searchLower = searchInput.toLowerCase().trim();
        const unselected = allOptions.filter((t) => !selectedTests.includes(t));
        if (!searchLower) return unselected.slice(0, 8);

        const exact: string[] = [], startsWith: string[] = [], wordStart: string[] = [], contains: string[] = [];
        for (const test of unselected) {
            const tl = test.toLowerCase();
            if (tl === searchLower) exact.push(test);
            else if (tl.startsWith(searchLower)) startsWith.push(test);
            else if (test.split(/[\s/]/).some((w) => w.toLowerCase().startsWith(searchLower))) wordStart.push(test);
            else if (tl.includes(searchLower)) contains.push(test);
        }
        return [...exact, ...startsWith, ...wordStart, ...contains].slice(0, 8);
    }, [searchInput, allOptions, selectedTests]);

    useEffect(() => {
        setFocusedIndex(showSuggestions && filteredSuggestions.length > 0 ? 0 : -1);
    }, [showSuggestions, filteredSuggestions.length]);

    const addInvestigation = useCallback((test: string) => {
        const trimmed = test.trim();
        if (trimmed && !selectedTests.includes(trimmed)) {
            setValue("investigations", [...rawSelected, trimmed], {shouldDirty: true});
        }
        setSearchInput("");
        setTimeout(() => {
            inputRef.current?.focus();
            setShowSuggestions(true);
        }, 0);
    }, [selectedTests, rawSelected, setValue]);

    const removeInvestigation = useCallback((testName: string) => {
        const updated = rawSelected.filter((item) => getInvestigationName(item) !== testName);
        setValue("investigations", updated, {shouldDirty: true});
    }, [rawSelected, setValue]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const max = filteredSuggestions.length - 1;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setFocusedIndex((p) => (p < max ? p + 1 : 0));
                break;
            case "ArrowUp":
                e.preventDefault();
                setFocusedIndex((p) => (p > 0 ? p - 1 : max));
                break;
            case "Enter":
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredSuggestions.length) {
                    addInvestigation(filteredSuggestions[focusedIndex]);
                } else if (searchInput.trim()) {
                    addInvestigation(searchInput.trim());
                }
                break;
            case "Escape":
                setShowSuggestions(false);
                break;
            case "Backspace":
                if (searchInput === "" && selectedTests.length > 0) {
                    e.preventDefault();
                    removeInvestigation(selectedTests[selectedTests.length - 1]);
                }
                break;
            case "Tab":
                setShowSuggestions(false);
                break;
        }
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="space-y-2" ref={containerRef}>
            <div
                className={cn(
                    "min-h-[42px] p-2 border border-border rounded-lg cursor-text transition-all bg-card flex items-center",
                    "hover:border-ring/60",
                    showSuggestions && "ring-2 ring-primary/20 border-primary",
                    errors.investigations && "border-destructive/30 bg-destructive/5"
                )}
                onClick={() => {
                    inputRef.current?.focus();
                    setShowSuggestions(true);
                }}
            >
                <div className="flex flex-wrap items-center gap-1.5 w-full">
                    {selectedTests.map((name, idx) => (
                        <Badge key={`${name}-${idx}`} variant="secondary"
                               className="h-6 px-2 text-xs bg-primary/10 text-primary border border-primary/30">
                            <span className="font-medium">{name}</span>
                            <button type="button" onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeInvestigation(name);
                            }} className="ml-1.5 p-1 -m-1 hover:text-destructive">
                                <X className="w-3 h-3"/>
                            </button>
                        </Badge>
                    ))}
                    <input
                        ref={inputRef} type="text" value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            setShowSuggestions(true);
                        }}
                        onFocus={() => setShowSuggestions(true)} onKeyDown={handleKeyDown}
                        placeholder={selectedTests.length === 0 ? "Add test..." : "Add another..."}
                        className="flex-1 min-w-[180px] h-7 bg-transparent border-0 outline-none text-xs text-foreground placeholder:text-muted-foreground/40 placeholder:font-normal"
                    />
                </div>
            </div>

            {showSuggestions && (filteredSuggestions.length > 0 || searchInput.trim()) && (
                <div
                    className="bg-popover border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto"
                    style={getDropdownStyle()}
                >
                    {filteredSuggestions.map((test, index) => (
                        <button
                            key={test} type="button"
                            onMouseDown={(e) => { e.preventDefault(); addInvestigation(test); }}
                            className={cn(
                                "w-full text-left px-3 py-2 text-xs border-b border-border/40 last:border-0 flex items-center justify-between",
                                index === focusedIndex
                                    ? "bg-primary/10 text-primary"
                                    : "hover:bg-accent text-foreground"
                            )}
                        >
                            <span>{test}</span>
                            <Plus className="w-3 h-3"/>
                        </button>
                    ))}
                            {searchInput.trim() && !filteredSuggestions.some(t => t.toLowerCase() === searchInput.toLowerCase().trim()) && (
                                <button type="button" onMouseDown={(e) => {
                                    e.preventDefault();
                                    addInvestigation(searchInput.trim());
                                }}
                                        className="w-full text-left px-3 py-2 text-xs border-t border-border flex items-center justify-between text-muted-foreground hover:bg-accent">
                                    <span>Add custom: "{searchInput.trim()}"</span>
                                    <Plus className="w-3 h-3 text-[hsl(var(--success))]"/>
                                </button>
                            )}
                </div>
            )}
        </div>
    );
}